import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { PiDownloadBold } from 'react-icons/pi';
import { useDispatch } from 'react-redux';
import { authSelectors } from '../../../../../app/auth/auth.selectors';
import { fetchInvoices } from '../../../../../app/page/invoices/invoices.actions';
import { invoicesPageSelectors } from '../../../../../app/page/invoices/invoices.selectors';
import { invoiceSelectors } from '../../../../../app/resource/invoice/invoice.selectors';
import { IInvoice } from '../../../../../app/resource/invoice/invoice.types';
import { userSelectors } from '../../../../../app/resource/user/user.selectors';
import { formatCentsAsCurrency } from '../../../../../utils/currency.utils';
import { formatUtcDateToUserTimeZone } from '../../../../../utils/date.utils';
import { useSelect, useSelectWithParams } from '../../../../../utils/selector.utils';
import { Pagination } from '../../../common/Pagination';

const PAGE_SIZE_OPTIONS = [1, 10, 25, 50];

const formatPeriod = (invoice: IInvoice, userTimezone: string | null) => {
  const start = formatUtcDateToUserTimeZone(invoice.periodStart, userTimezone);
  const end = formatUtcDateToUserTimeZone(invoice.periodEnd, userTimezone);
  if (start && end) {
    return `${start} – ${end}`;
  }
  if (end) {
    return end;
  }
  return '—';
};

export const Invoices = () => {
  const dispatch = useDispatch();
  const invoiceIds = useSelect(invoicesPageSelectors.selectInvoiceIds);
  const invoices = useSelectWithParams(invoiceSelectors.selectResourcesById, invoiceIds).filter(
    (invoice): invoice is IInvoice => invoice != null,
  );
  const currentPage = useSelect(invoicesPageSelectors.selectInvoicesPage);
  const totalPages = useSelect(invoicesPageSelectors.selectInvoicesTotalPages);
  const invoicesLimit = useSelect(invoicesPageSelectors.selectInvoicesLimit);
  const loggedUserId = useSelect(authSelectors.selectLoggedUser);
  const loggedUser = useSelectWithParams(userSelectors.selectResourceById, loggedUserId ?? '');
  const userTimezone = loggedUser?.timezone ?? null;

  const goToPage = (page: number) => {
    dispatch(fetchInvoices({ page }));
  };

  const onLimitChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const limit = Number(event.target.value);
      dispatch(fetchInvoices({ page: 1, limit }));
    },
    [dispatch],
  );

  return (
    <div className="col px-0">
      <div className="field-container px-2 mb-5">
        <div className="panel theme-gradient-2 py-1 px-2 flex aic jcsb mb-3">
          <h2 className="f-14 white bold">INVOICES</h2>
        </div>
        <div className="table-container" role="table" aria-label="Invoices">
          {invoices.length === 0 ? (
            <div className="f-14 blueGray text-center">No invoices found.</div>
          ) : (
            <>
              <div className="flex-table invoice-table header panel mb-1 aic py-0" role="rowgroup">
                <div className="flex-row" role="columnheader" style={{ width: '18%' }}>
                  DATE PAID
                </div>
                <div className="flex-row" role="columnheader" style={{ width: '18%' }}>
                  INVOICE
                </div>
                <div className="flex-row" role="columnheader" style={{ width: '34%' }}>
                  PERIOD
                </div>
                <div className="flex-row" role="columnheader" style={{ width: '15%' }}>
                  TOTAL
                </div>
                <div className="flex-row" role="columnheader" style={{ width: '15%' }}>
                  PDF
                </div>
              </div>
              {invoices.map((invoice) => {
                const receiptUrl = invoice.invoicePdf || invoice.hostedInvoiceUrl;
                return (
                  <div className="flex-table align-center invoice-table invoice-row" role="rowgroup" key={invoice.id}>
                    <Link
                      className="invoice-row-link"
                      to={`/settings/subscription/invoices/${encodeURIComponent(invoice.id)}`}
                      aria-label={`Invoice ${invoice.number}`}
                    />
                    <div className="flex-row" role="cell" style={{ width: '18%' }}>
                      {formatUtcDateToUserTimeZone(invoice.paidAt, userTimezone) ?? '—'}
                    </div>
                    <div className="flex-row" role="cell" style={{ width: '18%' }}>
                      <span className="f-14 bold">{invoice.number}</span>
                    </div>
                    <div className="flex-row" role="cell" style={{ width: '34%' }}>
                      <div className="f-14">{formatPeriod(invoice, userTimezone)}</div>
                    </div>
                    <div className="flex-row" role="cell" style={{ width: '15%' }}>
                      <div className="f-14 bold">{formatCentsAsCurrency(invoice.total, invoice.currency)}</div>
                    </div>
                    <div className="flex-row" role="cell" style={{ width: '15%' }}>
                      {receiptUrl ? (
                        <button
                          type="button"
                          className="btn btn-md green-bg invoice-row-action"
                          title="View PDF"
                          onClick={() => window.open(receiptUrl, '_blank', 'noopener,noreferrer')}
                          style={{ margin: 0 }}
                        >
                          <PiDownloadBold size={18} />
                        </button>
                      ) : (
                        <span className="f-14 blueGray">—</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </>
          )}
          {currentPage != null && totalPages != null && (
            <Pagination pageSelected={currentPage} onChange={goToPage} totalPages={totalPages} />
          )}
        </div>
      </div>
    </div>
  );
};
